import {
  Button,
  theme as defaultTheme,
  Icon,
  useSnackbar,
  Tooltip,
  TextField,
} from "@mindlercare/ui-web";
import { useFlag } from "@mindlercare/unleash-client";
import { SequentialUnlockItem } from "api/types";
import { useGetIcbtAssessment, usePutIcbtAssessment } from "api/useIcbtQueries";
import { useCallback, useEffect, useRef, useState } from "react";
import { TFunction, useTranslation } from "react-i18next";
import { styled } from "styled-components";
import { Typography } from "components/typography";
import { Timestamp } from "./Timestamp";
import { PhraseLibraryPicker } from "../../phraseLibrary/PhraseLibraryPicker";
import { ICBT_ASSESSMENTS_LIBRARY_ID } from "../../phraseLibrary/config";
import { useDemo } from "contexts/DemoContext";
import {
  FormContainer,
  InputContainer,
  TimestampContainer,
  Placeholder,
  CharCount,
  ActionContainer,
  ButtonWrapper,
} from "./sharedFormStyles";

const MAX_LENGTH = 2560;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  svg {
    color: ${defaultTheme.COLORS.GRAY};
  }
`;

const UnavailableText = styled(Typography)`
  color: ${defaultTheme.COLORS.ERROR};
`;

type AssessmentFormProps = {
  icbtRequestId: string;
  assessment: SequentialUnlockItem["assessment"];
};

type InferPlaceholderFromStateProps = {
  t: TFunction;
  status: SequentialUnlockItem["assessment"]["status"];
  isLoading?: boolean;
};

const inferPlaceholderFromState = ({
  t,
  isLoading,
  status,
}: InferPlaceholderFromStateProps) => {
  if (isLoading) return t("ASSESSMENT_INPUT_DISABLED_LOADING");
  if (status.status === "AWAITING_ASSESSMENT")
    return t("ASSESSMENT_INPUT_PLACEHOLDER");
  if (status.status === "UNAVAILABLE")
    return t(`ASSESSMENT_REASON.${status.reason}`);
  return t(`ASSESSMENT_STATUS.${status.status}`);
};

export const AssessmentForm = ({
  icbtRequestId,
  assessment,
}: AssessmentFormProps) => {
  const { t } = useTranslation();
  const [input, setInput] = useState<string>("");
  const { mutateAsync: putAssessment, isPending: isSending } =
    usePutIcbtAssessment();
  const { data, isLoading } = useGetIcbtAssessment(
    { icbtRequestId },
    {
      enabled:
        assessment.actions.view.success &&
        // Below is a hint to avoid sending too many audit events,
        // so we only fetch if right status and the draft already exists
        assessment.status.status === "AWAITING_ASSESSMENT" &&
        assessment.status.isDraft,
    },
  );
  const { enqueueSnackbar } = useSnackbar();
  const isDemo = useDemo();
  // Gated separately from the assessment feature itself (ICBT_ASSESSMENT_FIELD)
  // so the phrase library can be turned off without disabling the assessment.
  const isPhraseLibraryEnabled = useFlag("ICBT_ASSESSMENT_PHRASE_LIBRARY");

  const isEditingText = useRef(false);
  const isInited = useRef(false);

  useEffect(() => {
    const setInputAsync = async () => {
      if (data?.assessment_text) setInput(data?.assessment_text);
    };
    if (!isInited.current && data) {
      setInputAsync();
      isInited.current = true;
    }
  }, [data, setInput]);

  const handlePhraseInsert = useCallback(
    (body: string) => {
      const combined = input ? `${input}\n\n${body}` : body;
      if (combined.length > MAX_LENGTH) {
        enqueueSnackbar({ text: t("PHRASE_LIBRARY_INSERT_TOO_LONG") });
        return;
      }
      isEditingText.current = true;
      setInput(combined);
    },
    [input, enqueueSnackbar, t],
  );

  const draftIsUnchanged =
    input === data?.assessment_text || (!input && !data?.assessment_text);

  const sendAssessment = async (publish?: boolean) => {
    if (publish && !input) return;
    if (!publish && draftIsUnchanged) return;

    const snackbarText = publish
      ? t("ASSESSMENT_PUBLISHED")
      : t("ASSESSMENT_DRAFT_SAVED");

    try {
      await putAssessment({
        icbtRequestId,
        publish,
        assessmentText: input,
      });
      enqueueSnackbar({ text: snackbarText });
      if (publish) {
        setInput("");
      }
    } catch {
      enqueueSnackbar({ text: t("API_ERROR_GENERIC") });
    }
  };

  const isTextEditDisabled =
    isSending || isLoading || isDemo || !assessment.actions.update.success;
  const isPublishDisabled = isTextEditDisabled || !input;
  const isDraftDisabled = isTextEditDisabled || draftIsUnchanged;

  const updateDisabledReason = !assessment.actions.update.success
    ? t(`ASSESSMENT_REASON.${assessment.actions.update.reason}`)
    : null;

  if (!assessment.actions.view.success) {
    return (
      <FormContainer>
        <HeaderWrapper>
          <Icon.NoteAdd />
          <Typography variant="h3">{t("ASSESSMENT_HEADER")}</Typography>
        </HeaderWrapper>
        <UnavailableText variant="body-small">
          {t(`ASSESSMENT_REASON.${assessment.actions.view.reason}`)}
        </UnavailableText>
      </FormContainer>
    );
  }

  return (
    <FormContainer>
      <HeaderRow>
        <HeaderWrapper>
          <Icon.NoteAdd />
          <Typography variant="h3">{t("ASSESSMENT_HEADER")}</Typography>
        </HeaderWrapper>
        {/* Sits to the right of the title (wrapping under it, left-aligned, when
            the row is narrow). Only shown while the text field is actually
            editable — hidden (not just disabled) whenever the field is
            unavailable or blocked (demo, sending, loading, or no update
            permission), so there's nothing to insert into. */}
        {isPhraseLibraryEnabled && !isTextEditDisabled && (
          <PhraseLibraryPicker
            libraryId={ICBT_ASSESSMENTS_LIBRARY_ID}
            targetFieldLabel={t("PHRASE_LIBRARY_FIELD_ASSESSMENT")}
            onInsert={handlePhraseInsert}
          />
        )}
      </HeaderRow>
      <InputContainer>
        <TextField
          width="full"
          multiline
          unlimitedRows
          placeholder={inferPlaceholderFromState({
            t,
            isLoading,
            status: assessment.status,
          })}
          maxlength={MAX_LENGTH}
          hideSensitiveData={isDemo}
          value={input}
          onChange={(e) => {
            if (!isEditingText.current) isEditingText.current = true;
            const newValue = e.target.value;
            setInput(newValue);
          }}
          disabled={isTextEditDisabled}
        />
        {data ? (
          <TimestampContainer>
            <Timestamp
              alignSelf="center"
              status={data.published_at ? "sent" : "edited"}
            >
              {data.updated_at}
            </Timestamp>
          </TimestampContainer>
        ) : (
          <Placeholder />
        )}
        <CharCount>
          {input?.length ?? 0}/{MAX_LENGTH}
        </CharCount>
      </InputContainer>
      <ActionContainer>
        <ButtonWrapper>
          <Tooltip
            content={updateDisabledReason ?? t("SAVE_DRAFT_INFO")}
            disabled={updateDisabledReason ? false : isDraftDisabled}
          >
            <Button
              disabled={isDraftDisabled}
              variant="light"
              onClick={() => sendAssessment()}
            >
              {t("SAVE_DRAFT")}
            </Button>
          </Tooltip>
          <Tooltip
            content={updateDisabledReason ?? t("PUBLISH_ASSESSMENT_INFO")}
            disabled={updateDisabledReason ? false : isPublishDisabled}
          >
            <Button
              disabled={isPublishDisabled}
              variant="primary"
              onClick={() => sendAssessment(true)}
            >
              {t("PUBLISH_ASSESSMENT")}
            </Button>
          </Tooltip>
        </ButtonWrapper>
      </ActionContainer>
    </FormContainer>
  );
};
